import React, { useState } from 'react';
import Image from 'next/image';
import { FieldValues, UseFormRegister, UseFormSetValue } from 'react-hook-form';
import Icons from '@/const/icons';
import {
	Avatar,
	Button,
	ErrorMessageInput,
	Icon,
	Typography,
} from '@/components/common';
import { ModalUploadImage } from './ModalUploadImage';
import { useNotification } from '@/hooks';

interface Props {
	file: string;
	firstname: string;
	lastname: string;
	prevFile: string | undefined;
	error?: any;
	rules?: Record<string, unknown>;
	register: UseFormRegister<FieldValues>;
	setValue: UseFormSetValue<FieldValues>;
	removePrevFile: () => void;
}

export const FormInputPicture: React.FC<Props> = ({
	firstname,
	lastname,
	file,
	prevFile,
	register,
	rules,
	error,
	setValue,
	removePrevFile,
}) => {
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const [srcImg, setSrcImg] = useState<string | null>();
	const { toastSuccess } = useNotification();

	const handleSetFile = (url: string | null, file: File | null): void => {
		setSrcImg(url);
		setValue('file', file);
	};

	const handleRemovePrevFile = (): void => {
		toastSuccess('Your photo has been successfully deleted.');
		setValue('file', null);
		setSrcImg('');
		removePrevFile();
	};
	React.useEffect(() => {
		if (file && typeof file === 'string') {
			setSrcImg(file);
		}
	}, [file]);

	const getAvatarImage = (): string => {
		if (!firstname) return Icons.avatar;
		if (srcImg) return srcImg;
		if (file) return file;
		return '';
	};

	return (
		<>
			<div className="flex items-center gap-4">
				<Avatar
					photoUrl={getAvatarImage()}
					className="h-10 w-10"
					firstname={firstname}
					lastname={lastname}
				/>
				<div>
					<Button
						decoration="secondary"
						className="flex items-center gap-2 bg-white h-[30px]"
						onClick={() => {
							setIsModalOpen(!isModalOpen);
							removePrevFile();
							setValue('file', null);
						}}
					>
						<Icon src={Icons.documentUpload} className="!w-4 !h-4" />
						<Typography type="semibold-16">Upload picture</Typography>
					</Button>
				</div>
				{srcImg && (
					<button type="button" onClick={handleRemovePrevFile}>
						<Image
							src={Icons.trashPNG}
							alt="delete profile picture"
							width={16}
							height={16}
							unoptimized
						/>
					</button>
				)}
				<ModalUploadImage
					file={file}
					prevFile={prevFile}
					register={register}
					isOpen={isModalOpen}
					setImage={handleSetFile}
					setIsModalOpen={setIsModalOpen}
					rules={rules}
					showPrevFile={false}
				/>
			</div>
			{error && error.message && <ErrorMessageInput message={error.message} />}
		</>
	);
};
